import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Compass, CheckCircle2, AlertTriangle } from 'lucide-react';
import Header from '../components/Header';
import SEO from '../components/SEO';
import advisor from '../lib/advisor';
import { API_BASE } from '../lib/api';

/**
 * Comparador Inteligente del Habita Advisor.
 *
 * El Advisor no elige un ganador. Muestra fortalezas, debilidades y
 * en qué escenario cada opción sería preferible. Se basa en el Context
 * del usuario (misma fuente que las recomendaciones).
 */

function ItemColumn({ item, isTop }) {
  const s = item.snapshot || {};
  return (
    <div
      className={`rounded-2xl border ${isTop ? 'border-habita bg-habita-mint/10' : 'border-gray-200 bg-white'} p-4`}
      data-testid={`compare-item-${item.item_id}`}
    >
      <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">
        {isTop ? 'Se ajusta mejor a ti' : 'Otra opción'}
      </p>
      <Link
        to={`/propiedad/${item.item_id}`}
        className="block font-serif text-lg font-semibold text-habita-dark hover:text-habita line-clamp-2"
      >
        {s.title || 'Propiedad'}
      </Link>
      <p className="text-xs text-gray-500 mt-0.5">{s.city} · {s.type} · {s.operation}</p>
      {s.price && (
        <p className="mt-1 text-sm font-semibold text-habita">
          {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(s.price)}
          {s.operation === 'arriendo' && <span className="text-xs text-gray-500 font-normal ml-1">/mes</span>}
        </p>
      )}
      <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
        <div><p className="text-gray-500">Habitac.</p><p className="font-semibold">{s.beds ?? '—'}</p></div>
        <div><p className="text-gray-500">Área</p><p className="font-semibold">{s.area ? `${s.area} m²` : '—'}</p></div>
        <div><p className="text-gray-500">Encaje</p><p className="font-semibold">{Math.round((item.fit_score || 0) * 100)}%</p></div>
      </div>

      {item.strengths?.length > 0 && (
        <div className="mt-4">
          <p className="text-[11px] uppercase tracking-widest text-emerald-700 mb-1.5 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Fortalezas
          </p>
          <ul className="space-y-1 text-xs text-gray-700">
            {item.strengths.map((s2, i) => <li key={i} className="pl-3 relative">
              <span className="absolute left-0 text-emerald-600">•</span>{s2}
            </li>)}
          </ul>
        </div>
      )}

      {item.weaknesses?.length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] uppercase tracking-widest text-amber-700 mb-1.5 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> A tener en cuenta
          </p>
          <ul className="space-y-1 text-xs text-gray-700">
            {item.weaknesses.map((s2, i) => <li key={i} className="pl-3 relative">
              <span className="absolute left-0 text-amber-600">•</span>{s2}
            </li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function ComparePage() {
  const [params] = useSearchParams();
  const ids = (params.get('ids') || '').split(',').filter(Boolean).slice(0, 4);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (ids.length < 2) {
        setError('Necesitas al menos 2 propiedades para comparar.');
        setLoading(false); return;
      }
      // Asegura un Context (si el usuario aterrizó directamente)
      await advisor.getOrStartContext({ entryPoint: 'other', intent: 'comparar propiedades' });
      const ctxId = advisor.getActiveContextId();
      if (!ctxId) {
        setError('El Advisor no está disponible en este momento.');
        setLoading(false); return;
      }
      try {
        const r = await fetch(`${API_BASE}/advisor/comparisons`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ context_id: ctxId, item_type: 'property', item_ids: ids }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!cancelled) setComparison(data);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [params.toString()]);

  const orderedItems = comparison?.items
    ? [...comparison.items].sort((a, b) => b.fit_score - a.fit_score)
    : [];
  const topId = orderedItems[0]?.item_id;

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Comparar propiedades"
        description="Compara hasta 3 propiedades lado a lado con la voz del Habita Advisor. Pros, contras y encaje con lo que buscas — sin sesgo comercial."
        noindex
      />
      <Header />
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6">
        <Link to="/buscar" className="text-sm text-gray-500 hover:text-habita inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> Volver a los resultados
        </Link>

        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-full bg-habita text-white grid place-items-center">
              <Compass className="w-4 h-4" />
            </div>
            <p className="text-[11px] uppercase tracking-widest text-habita-dark font-semibold">
              Habita Advisor · Comparador Inteligente
            </p>
          </div>
          <h1 className="font-serif text-3xl font-bold text-habita-dark">
            ¿Cuál se ajusta mejor a ti?
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Estas son las diferencias que pueden ayudarte a decidir. No hay una respuesta única —
            depende de lo que priorices en tu búsqueda.
          </p>
        </div>

        {loading && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {ids.map((_, i) => <div key={i} className="h-72 rounded-2xl bg-gray-100 animate-pulse" />)}
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
            {error}
          </div>
        )}

        {!loading && comparison && (
          <>
            <div className="mb-6 rounded-2xl bg-gradient-to-br from-habita-mint/50 via-white to-white border border-habita/20 p-5" data-testid="advisor-verdict">
              <p className="text-[15px] text-habita-dark leading-relaxed">
                {comparison.verdict.human_readable}
              </p>
              {comparison.verdict.reasoning_steps?.length > 0 && (
                <ul className="mt-3 space-y-1.5 text-sm text-gray-700">
                  {comparison.verdict.reasoning_steps.slice(1).map((s, i) => (
                    <li key={i} className="pl-4 relative">
                      <span className="absolute left-0 top-1.5 w-1.5 h-1.5 rounded-full bg-habita" />
                      {s}
                    </li>
                  ))}
                </ul>
              )}
              {comparison.key_priorities?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-gray-500">Tus prioridades detectadas:</span>
                  {comparison.key_priorities.slice(0, 4).map((k) => (
                    <span key={k} className="text-[11px] font-medium bg-white border border-habita/30 rounded-full px-2.5 py-0.5">{k}</span>
                  ))}
                </div>
              )}
              <p className="mt-4 text-[10px] text-gray-400">
                Comparación determinística · {comparison.dimensions_evaluated} dimensiones · {comparison.elapsed_ms}ms
              </p>
            </div>

            <div className={`grid gap-4 ${orderedItems.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
              {orderedItems.map((it) => (
                <ItemColumn key={it.item_id} item={it} isTop={it.item_id === topId} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
